// Iteration 088 -- Reusable claim-an-offer dialog
import { useEffect, useState } from "react";
import type { SponsorOfferPublic, ClaimOfferInput } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const CONTACT_TIMES = [
  "Now / as soon as possible",
  "Weekday hours",
  "Evenings",
  "Weekends",
];

interface ClaimOfferDialogProps {
  offer: SponsorOfferPublic;
  schoolId?: string | null; // if set, claims as school_admin on behalf of a school
  onClose: () => void;
  onClaimed?: () => void;
}

export function ClaimOfferDialog({ offer, schoolId, onClose, onClaimed }: ClaimOfferDialogProps) {
  const { user } = useAuth();
  const me = user?.user;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [contactTime, setContactTime] = useState(CONTACT_TIMES[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Pre-fill from profile
    if (me) {
      setName([me.first_name, me.last_name].filter(Boolean).join(" ") || me.display_name || "");
      setEmail(me.email ?? "");
      setPhone(me.phone_number ?? "");
    }
  }, [me]);

  async function submit() {
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: ClaimOfferInput = {
        contact_name: name.trim(),
        contact_email: email.trim(),
        contact_phone: phone.trim() || null,
        preferred_contact_time: contactTime,
        notes: note.trim() || null,
      };
      if (schoolId) payload.school_id = schoolId;
      await api.claimOffer(offer.id, payload);
      setDone(true);
      onClaimed?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to submit claim");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-start" data-dialog="claim-offer">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto sm:mt-16">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">{offer.title}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {offer.sponsor_name}
                {offer.discount_value && ` · ${offer.discount_value}`}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {done ? (
            <div className="space-y-3 py-4 text-center">
              <p className="text-sm font-medium">Thanks -- your details are on their way.</p>
              <p className="text-xs text-muted-foreground">
                {offer.sponsor_name} will contact you {contactTime.toLowerCase()}.
              </p>
              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{offer.description}</p>

              <div className="space-y-1.5">
                <Label htmlFor="claim-name">Name</Label>
                <Input id="claim-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="claim-email">Email</Label>
                <Input id="claim-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="claim-phone">Phone</Label>
                <Input id="claim-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="claim-time">Preferred contact time</Label>
                <select
                  id="claim-time"
                  title="Preferred contact time"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={contactTime}
                  onChange={(e) => setContactTime(e.target.value)}
                >
                  {CONTACT_TIMES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="claim-note">Notes (optional)</Label>
                <textarea
                  id="claim-note"
                  title="Notes"
                  placeholder="Anything else the sponsor should know"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-destructive" data-error>{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    offer.call_to_action_label || "Claim Offer"
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
